import type { CustomFile } from '../types';

// ----------------------------------------------------------------------------

export type TypePausedFlag = {
    paused: boolean;
    pause: () => void;
    resume: () => void;
    waitForResume: () => Promise<void>;
};

export type TypeUploadContext = {
    abortAll?: () => void;
    abortedFlag: { aborted: boolean };
    pausedFlag: TypePausedFlag;
    completedFiles: { value: number };
    errorCount: number;
    successCount: number;
    skippedCount: number;
    totalBytes: number;
    totalFiles: number;
    uploadedBytes: { value: number };
    xhrList: XMLHttpRequest[];
};

// ----------------------------------------------------------------------------

/*
 * Pause flag the workers await before each file. It cannot interrupt a request already in
 * flight, so pausing takes effect from the next file onwards.
 */
function createPausedFlag(): TypePausedFlag {
    let paused = false;
    let resolver: (() => void) | null = null;
    let pausePromise: Promise<void> | null = null;

    return {
        get paused() {
            return paused;
        },
        pause(): void {
            if (paused) {
                return;
            }

            paused = true;
            pausePromise = new Promise<void>((resolve) => {
                resolver = resolve;
            });
        },
        resume(): void {
            if (!paused) {
                return;
            }

            paused = false;
            resolver?.();
            resolver = null;
            pausePromise = null;
        },
        async waitForResume(): Promise<void> {
            if (pausePromise) {
                await pausePromise;
            }
        },
    };
}

/*
 * State shared by every request of one upload run: the abort and pause flags, the list of
 * live XHRs, and the counters and byte totals that progress is derived from. Created by the
 * entry point and threaded through the orchestrator down to each attempt.
 */
function createUploadContext(files: CustomFile[]): TypeUploadContext {
    return {
        abortedFlag: { aborted: false },
        pausedFlag: createPausedFlag(),
        completedFiles: { value: 0 },
        errorCount: 0,
        successCount: 0,
        skippedCount: 0,
        totalBytes: files.reduce((sum, f) => sum + f.size, 0),
        totalFiles: files.length,
        uploadedBytes: { value: 0 },
        xhrList: [],
    };
}

// ----------------------------------------------------------------------------

export default createUploadContext;
